import { useEffect, useRef, useState } from "react";
import { BellRing, CheckCheck, Download, Loader2, Mail, Send, Smartphone, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../../../connect/api";
import { ago, Avatar, Empty, Header, roleLine, Spinner } from "../ui";

const STAGES = [
  { label: "Not installed", cls: "text-slate-500 border-slate-700" },
  { label: "App installed", cls: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  { label: "First push delivered", cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  { label: "Confirmed on device", cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
];

const Funnel = ({ t }) => (
  <div className="grid grid-cols-3 gap-2 text-xs" data-testid="install-funnel">
    {[["Installed", t.installed, Download, "text-sky-300"], ["First push", t.first_push, Send, "text-amber-300"], ["Confirmed", t.confirmed, CheckCheck, "text-emerald-300"]].map(([l, n, I, c]) => (
      <div key={l} className="nxc-card p-3">
        <div className="flex items-center gap-1.5 text-slate-400"><I size={12} /> {l}</div>
        <div className={`font-semibold text-lg mt-0.5 ${n ? c : "text-slate-500"}`} data-testid={`funnel-${l.toLowerCase().replace(" ", "-")}`}>{n}<span className="text-slate-500 text-xs font-normal"> / {t.staff}</span></div>
      </div>
    ))}
  </div>
);

const StageLine = ({ i }) => {
  const s = STAGES[i.install_stage];
  const when = i.install_stage === 3 ? i.first_received_at : i.install_stage === 2 ? i.first_push_at : i.installed_at;
  const android = i.devices.find((d) => d.kind === "android");
  return (
    <div className="text-[11px] mt-1 flex gap-2 flex-wrap items-center">
      <span className={`border rounded-full px-2 py-0.5 ${s.cls}`} data-testid={`install-stage-${i.email}`}>{s.label}{when ? ` · ${ago(when)}` : ""}</span>
      {android && <span className="text-slate-500 truncate max-w-[140px]">{android.ua}{android.app_version ? ` · v${android.app_version}` : ""}</span>}
      {i.install_stage === 3 && i.last_received_at && <span className="text-slate-500">last seen {ago(i.last_received_at)}</span>}
      {i.install_stage === 0 && i.nudge_at && <span className="text-slate-500" data-testid={`nudged-${i.email}`}>reminded {ago(i.nudge_at)}{i.nudge_count > 1 ? ` (${i.nudge_count}×)` : ""}</span>}
    </div>
  );
};

const ApkCard = ({ info, onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const up = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f); fd.append("version", (f.name.match(/(\d+\.\d+(\.\d+)?)/) || [])[1] || "");
    try { const { data } = await api.post("/admin/app/apk", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`APK v${data.version} hosted (${(data.size / 1048576).toFixed(1)} MB)`); onDone(); } catch (e) { toast.error(errText(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="nxc-card p-3 text-xs" data-testid="apk-card">
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-slate-400">Android APK for staff download</div>
          <div className={`font-semibold mt-0.5 ${info?.hosted ? "text-emerald-300" : "text-amber-300"}`} data-testid="apk-status">{info?.hosted ? `v${info.version} · ${(info.size / 1048576).toFixed(1)} MB · ${info.downloads || 0} downloads` : "Not uploaded — nudge emails will point to the web app"}</div>
          {info?.hosted && <div className="text-slate-500 mt-0.5 break-all">{window.location.origin}/api/connect/app/download</div>}
        </div>
        <button onClick={() => ref.current?.click()} disabled={busy} className="nxc-btn nxc-btn-ghost h-9 text-xs shrink-0" data-testid="apk-upload-button">{busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {info?.hosted ? "Replace" : "Upload APK"}</button>
        <input ref={ref} type="file" accept=".apk" className="hidden" onChange={(e) => up(e.target.files?.[0])} data-testid="apk-file-input" />
      </div>
    </div>
  );
};

export const AdminDevices = () => {
  const [d, setD] = useState(null);
  const [apk, setApk] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => Promise.all([api.get("/admin/push-devices").then((r) => setD(r.data)), api.get("/app/info").then((r) => setApk(r.data))]);
  useEffect(() => { load(); }, []);
  const nudge = async (emails) => {
    setBusy(emails ? emails[0] : "__all__");
    try { const { data } = await api.post("/admin/push-devices/nudge", { emails }); toast.success(data.sent.length ? `Reminder emailed to ${data.sent.length} staff${data.failed.length ? ` · ${data.failed.length} failed` : ""}` : "Nobody to remind — everyone has installed the app"); load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const test = async (email) => {
    setBusy(email);
    try { const { data } = await api.post("/admin/push-devices/test", null, { params: { email } }); toast.success(`Test sent to ${data.sent} device${data.sent === 1 ? "" : "s"}`); load(); } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  return (
    <div className="h-full flex flex-col">
      <Header title="App installs & push" sub={d ? `${d.totals.fcm} Android app · ${d.totals.web} browser` : ""} back="/connect/admin" />
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="admin-devices">
        {!d ? <div className="flex justify-center py-10"><Spinner /></div> : (
          <>
            <Funnel t={d.totals} />
            <ApkCard info={apk} onDone={load} />
            <button onClick={() => nudge(null)} disabled={!!busy || d.totals.installed >= d.totals.staff - 1} className="nxc-btn nxc-btn-gold w-full h-10 text-xs" data-testid="nudge-all-button">{busy === "__all__" ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Email everyone who hasn't installed ({Math.max(d.totals.staff - 1 - d.totals.installed, 0)})</button>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`nxc-card p-3 ${d.web_push_configured ? "" : "!border-red-500/40"}`}>Web Push (PWA) <div className={`font-semibold mt-0.5 ${d.web_push_configured ? "text-emerald-300" : "text-red-300"}`}>{d.web_push_configured ? "Configured" : "Not configured"}</div></div>
              <div className={`nxc-card p-3 ${d.fcm_configured ? "" : "!border-amber-500/40"}`}>Firebase (APK) <div className={`font-semibold mt-0.5 ${d.fcm_configured ? "text-emerald-300" : "text-amber-300"}`}>{d.fcm_configured ? "Configured" : "Awaiting keys"}</div></div>
            </div>
            <p className="text-[11px] text-slate-500 px-1">Installed = the app registered its phone · First push = Google accepted a notification for it · Confirmed = the phone reported the notification arrived or was opened.</p>
            {d.items.length === 0 && <Empty icon={Smartphone} title="No staff yet" />}
            <ul className="space-y-1.5">
              {d.items.map((i) => (
                <li key={i.email} className="nxc-card p-3" data-testid={`device-row-${i.email}`}>
                  <div className="flex items-center gap-3">
                    <Avatar name={i.staff.name} src={i.staff.avatar} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-100 truncate">{i.staff.name}</div>
                      <div className="text-[11px] text-slate-400 truncate">{roleLine(i.staff)}</div>
                      <StageLine i={i} />
                      <div className="text-[11px] mt-1 flex gap-2 flex-wrap">
                        <span className={i.web ? "text-emerald-300" : "text-slate-500"}>{i.web} browser</span>
                        <span className={i.fcm ? "text-emerald-300" : "text-slate-500"}>{i.fcm} Android</span>
                        {i.push_count > 0 && <span className="text-slate-500">· {i.push_count} push{i.push_count === 1 ? "" : "es"} sent</span>}
                      </div>
                    </div>
                    {i.install_stage === 0 && !i.staff.is_ceo ? (
                      <button onClick={() => nudge([i.email])} disabled={!!busy} className="nxc-btn nxc-btn-ghost h-9 text-xs" data-testid={`device-nudge-${i.email}`}>{busy === i.email ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />} Remind</button>
                    ) : (
                      <button onClick={() => test(i.email)} disabled={!!busy || (i.web + i.fcm === 0)} className="nxc-btn nxc-btn-ghost h-9 text-xs" data-testid={`device-test-${i.email}`}>{busy === i.email ? <Loader2 size={13} className="animate-spin" /> : <BellRing size={13} />} Test</button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};
